import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
	Box,
	Skeleton,
	Tab,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Alert,
	AlertTitle,
	useTheme,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroLink, CerebroPageContainer, CerebroText } from '../../../shared/components';
import { useGetTermSheetQuery, useGetTermSheetRevisionQuery } from '../../../shared/hooks';
import { CovenantsPage } from './CovenantsPage';
import { FacilityList } from './FacilityList';
import { GeneralForm } from './GeneralForm';
import { GovernanceForm } from './GovernanceForm';
import { ReportingPage } from './ReportingPage';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { ConditionsPage } from './ConditionsPage';

export const RevisionForm: React.FC = () => {
	const { guid, termSheetGuid, revisionGuid } = useParams();
	const sheetQuery = useGetTermSheetQuery(guid, termSheetGuid, {
		refetchOnWindowFocus: false,
	});
	const revisionQuery = useGetTermSheetRevisionQuery(guid, termSheetGuid, revisionGuid, {
		refetchOnWindowFocus: false,
	});
	const theme = useTheme();

	const [tab, setTab] = React.useState('general');
	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const costOfCapital = (revisionQuery.data?.costOfCapital ?? 0.0) * 100.0;

	return (
		<CerebroPageContainer maxWidth={'100%'}>
			<CerebroBreadcrumb>Term Sheets</CerebroBreadcrumb>

			{revisionQuery.isSuccess && sheetQuery.isSuccess && (
				<>
					{revisionQuery.data.incompleteData && revisionQuery.data.incompleteData.length > 0 && (
						<Alert severity="warning" sx={{ borderWidth: 1, borderColor: theme.palette.warning.dark }}>
							<AlertTitle>Missing Information</AlertTitle>
							{revisionQuery.data.incompleteData.map(message => (
								<CerebroText key={message}>{message}</CerebroText>
							))}
						</Alert>
					)}

					<TableContainer sx={{ marginBottom: 5, width: '80%' }}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Version #</TableCell>
									<TableCell>Bank</TableCell>
									<TableCell>Lender</TableCell>
									<TableCell>Cost of Capital</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									<TableCell>{revisionQuery.data.versionNumber}</TableCell>
									<TableCell>{sheetQuery.data.bankName}</TableCell>
									<TableCell>{sheetQuery.data.lenderName}</TableCell>
									<TableCell>{costOfCapital.toFixed(2)}%</TableCell>
									<TableCell>
										{revisionQuery.data.termSheetFileName && (
											<CerebroLink
												href={CerebroApiEndpoints.TermSheets.AbsoluteDownload(
													guid,
													sheetQuery.data?.guid,
													revisionQuery.data.guid
												)}
												target="_blank"
												rel="noreferrer"
												variant="link"
											>
												Download
											</CerebroLink>
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="General" value="general" />
						<Tab label="Facilities" value="facilities" />
						<Tab label="Governance" value="governance" />
						<Tab label="Covenants" value="covenants" />
						<Tab label="Reporting" value="reporting" />
						<Tab label="Other Conditions" value="conditions" />
					</TabList>
				</Box>

				{sheetQuery.isLoading && revisionQuery.isLoading && (
					<>
						<Skeleton height={200} />
						<Skeleton height={200} />
						<Skeleton height={200} />
					</>
				)}

				{sheetQuery.isSuccess && revisionQuery.isSuccess && (
					<>
						<TabPanel value="general">
							<GeneralForm
								dataRoomGuid={guid}
								termSheetGuid={termSheetGuid}
								revisionGuid={revisionGuid}
								termSheet={sheetQuery.data}
								revision={revisionQuery.data}
								isLoading={
									sheetQuery.isLoading ||
									sheetQuery.isFetching ||
									sheetQuery.isRefetching ||
									revisionQuery.isLoading ||
									revisionQuery.isFetching ||
									revisionQuery.isRefetching
								}
							/>
						</TabPanel>

						<TabPanel value="facilities">
							<FacilityList
								dataRoomGuid={guid}
								termSheetGuid={termSheetGuid}
								revisionGuid={revisionGuid}
								termSheet={sheetQuery.data}
								revision={revisionQuery.data}
								isLoading={
									sheetQuery.isLoading ||
									sheetQuery.isFetching ||
									sheetQuery.isRefetching ||
									revisionQuery.isLoading ||
									revisionQuery.isFetching ||
									revisionQuery.isRefetching
								}
							/>
						</TabPanel>

						<TabPanel value="governance">
							<GovernanceForm
								dataRoomGuid={guid}
								termSheetGuid={termSheetGuid}
								revisionGuid={revisionGuid}
								revision={revisionQuery.data}
								isLoading={
									sheetQuery.isLoading ||
									sheetQuery.isFetching ||
									sheetQuery.isRefetching ||
									revisionQuery.isLoading ||
									revisionQuery.isFetching ||
									revisionQuery.isRefetching
								}
							/>
						</TabPanel>

						<TabPanel value="covenants">
							<CovenantsPage
								dataRoomGuid={guid}
								termSheetGuid={termSheetGuid}
								revisionGuid={revisionGuid}
								revision={revisionQuery.data}
								isLoading={
									sheetQuery.isLoading ||
									sheetQuery.isFetching ||
									sheetQuery.isRefetching ||
									revisionQuery.isLoading ||
									revisionQuery.isFetching ||
									revisionQuery.isRefetching
								}
							/>
						</TabPanel>

						<TabPanel value="reporting">
							<ReportingPage
								dataRoomGuid={guid}
								termSheetGuid={termSheetGuid}
								revisionGuid={revisionGuid}
								revision={revisionQuery.data}
								isLoading={
									sheetQuery.isLoading ||
									sheetQuery.isFetching ||
									sheetQuery.isRefetching ||
									revisionQuery.isLoading ||
									revisionQuery.isFetching ||
									revisionQuery.isRefetching
								}
							/>
						</TabPanel>

						<TabPanel value="conditions">
							<ConditionsPage
								dataRoomGuid={guid}
								termSheetGuid={termSheetGuid}
								revisionGuid={revisionGuid}
								revision={revisionQuery.data}
								isLoading={
									sheetQuery.isLoading ||
									sheetQuery.isFetching ||
									sheetQuery.isRefetching ||
									revisionQuery.isLoading ||
									revisionQuery.isFetching ||
									revisionQuery.isRefetching
								}
							/>
						</TabPanel>
					</>
				)}
			</TabContext>
		</CerebroPageContainer>
	);
};
